/**
 * "Why this" — the Explain verb for one queued item (bata-ui#148 step 3.3, `docs/adr/6005`).
 *
 * Three queues, three kinds of explanation, one rule: **every figure here is a field the
 * platform published on the row, and nothing is re-derived.**
 *
 *   PROPOSAL    `RecommendationCard` on the live `ProposalSummary`: what the doors requested
 *               beside what the engine proposed, the constraint that bound the line, its
 *               expected impact in its own currency, and the signed drivers (platform ADR
 *               6038) — which sum exactly to the units proposed, so the list reads as
 *               arithmetic rather than as a story.
 *   PRICE STEP  the step's own figures, and the ways into the markdown views that explain a
 *               price path for that option: the Output view's solved path and the
 *               Explanation view's elasticity. The platform's `/markdown/diagnose` and
 *               `/markdown/analogues` reads have no screen in this build — both take some
 *               twenty caller-supplied tunables a price step does not carry — so they are a
 *               stated absence, not a link to a page that does not exist.
 *   TRANSFER    lane cost, run damage and net benefit, as money in the row's currency.
 *
 * ── A PLATFORM THAT PUBLISHES NO DECOMPOSITION ─────────────────────────────
 *
 * Before platform v0.47 (ADR 6038) a proposal's `drivers` column held the engine's
 * `bound_by` restated and its contributions were empty, so the reader published NO driver.
 * Rows written before the upgrade stay that way (no backfill). An empty list therefore reads
 * "the platform version that wrote this run publishes no decomposition" — never an empty
 * chart, never a zero, and never a contribution this file made up.
 */

import { useTranslation } from 'react-i18next';
import { ArrowRight, X } from 'lucide-react';

import { rankedPriceStepsReport, runProvenance } from '@/api/generated';
import type { PriceActionSummary, ProposalSummary, TransferSummary } from '@/api/generated';
import { useApiQuery, useChannelLabel } from '@/api';
import { formatCurrency, formatInteger, formatPercent, displayNumberFormat } from '@/lib/format';
import {
  InAppAnchor, RecommendationCard, sizeName, useSizeText,
  type Lineage, type MeasureDefinition, type ReasonCode, type Recommendation,
} from '@/planning';
import { AboutPopover, Badge, Button, Skeleton, StatusIndicator } from '@/primitives';

import { proposalGrain, toConstraintKey, toPurposeKey } from './api';
import { DROPPED_DOOR_KEY, isDroppedDoorLine } from './dropped-door';
import { forecastModelKnown, forecastModelWords } from './forecast-words';
import { useProposalSizeText } from './dropped-door-cells';
import { governanceOf, parameterHref, parameterLabelKey } from './governing-parameters';
import { Panel } from './panels';
import { capOfRun, plainAnswer, rowLinesOf, type AnswerPhrase } from './plain-answer';
import { dcPool, rationingRank, sizeCurveShare, type Recorded } from './rationing';
import { FlaggedBasisBadges } from './PriceBasis';
import { PriceRoundingFacts } from './PriceRounding';
import { PriceStepWhy, stepWhen } from './PriceStepWhy';
import { TransferWhy } from './TransferWhy';
import {
  closeExplain, COMPARE_KIND_PARAM, EXPLAIN_PARAM, EXPLAIN_VIEW_PARAM, MARKDOWN_VIEW_PARAM,
  navigateToScope, OPEN_OPTION_PARAM, OPTION_KEY_PARAM_LEGACY, OPTION_KEYS_PARAM, openExplain,
} from './scope';
import { dispositionStateKey } from './transfer-dispositions';

// ── drivers ─────────────────────────────────────────────────────────────────

/**
 * The nine driver codes platform ADR 6038 publishes, and ADR 6188's `shipment_cost`, each a catalogue key. Spelled out as
 * literals so the catalogue check sees every key — nothing is built by interpolation.
 *
 * Six are the NEED decomposition (forecast, buffer, floor, stock, inbound, whole units) and
 * the last four are the ENGINE's change to that need on a given line; `shipment_cost` is the pairs
 * the transport cost (shipment, carton, pallet) took off after the solve.
 */
const DRIVER_KEY: Readonly<Record<string, string>> = {
  forecast_rate: 'inseason.driver.forecast_rate',
  cover_target: 'inseason.driver.cover_target',
  presentation_minimum: 'inseason.driver.presentation_minimum',
  on_hand: 'inseason.driver.on_hand',
  in_transit: 'inseason.driver.in_transit',
  whole_units: 'inseason.driver.whole_units',
  case_rounding: 'inseason.driver.case_rounding',
  dc_availability: 'inseason.driver.dc_availability',
  shipment_cost: 'inseason.driver.shipment_cost',
  allocation: 'inseason.driver.allocation',
};

/**
 * A code this build does not name. The platform can add one without a release (G5), so it
 * renders as "unnamed driver ({code})" — the raw code is a datum, and a plausible wrong name
 * for it would be a lie about why the line has the quantity it has.
 */
export const UNCLASSIFIED_DRIVER_KEY = 'inseason.driver.unclassified';

/** DERIVE — a published driver into the card's reason code. */
export function toDriverReason(driver: { readonly code: string; readonly contribution: number }): ReasonCode {
  const labelKey = DRIVER_KEY[driver.code];
  return labelKey === undefined
    ? { code: driver.code, labelKey: UNCLASSIFIED_DRIVER_KEY, labelValues: { code: driver.code }, contribution: driver.contribution }
    : { code: driver.code, labelKey, contribution: driver.contribution };
}

/**
 * Whether the drivers add up to the units proposed — the property ADR 6038 promises and
 * property-tests. Checked rather than assumed: a list that did not balance would be
 * explaining a different number, and the planner is told so.
 */
export type DriverBalance =
  | { readonly kind: 'none' }
  | { readonly kind: 'balanced' }
  | { readonly kind: 'unbalanced'; readonly sum: number };

export function driverBalance(proposal: ProposalSummary): DriverBalance {
  const drivers = proposal.drivers ?? [];
  if (drivers.length === 0) return { kind: 'none' };
  const sum = drivers.reduce((total, driver) => total + driver.contribution, 0);
  return Math.abs(sum - proposal.units) < 1e-6 ? { kind: 'balanced' } : { kind: 'unbalanced', sum };
}

/**
 * GAP — `ProposalSummary` publishes no lineage, as the allocation screen records. The measure
 * is this repository's own statement of what the figure means, not a claim about provenance.
 */
const PROPOSAL_LINEAGE: Lineage = {
  measure: 'units',
  definition: 'Units the engine proposes to move to this door, against the units the door requested.',
  registerVersion: '',
  sourceBatch: '',
  planVersionId: '',
};

/**
 * The measure the card's two figures are in: pairs, and no polarity — proposing fewer than
 * requested is neither good nor bad without knowing why, which is what the drivers say. No
 * register version is claimed: the proposal publishes none.
 */
const PROPOSAL_UNITS: MeasureDefinition = {
  key: 'units',
  labelKey: 'inseason.why.proposed',
  unitKey: 'inseason.unit.pairs',
  polarity: 'none',
  editable: false,
  registerVersion: '',
};

/**
 * TRANSFORM — a published proposal into the card's recommendation.
 *
 * `requested_units` is what the doors asked for and `units` is what the engine proposed
 * (the platform's `ProposalRow` note: transposing them makes a run that met a tenth of its
 * need read as one that met all of it). Confidence and model version stay `null` where the
 * platform sends none. `size` is the line's size as words (`useSizeText()` over
 * `sizeName(proposal)`), never the opaque `size_key` (`docs/adr/9027`).
 */
export function proposalRecommendation(
  proposal: ProposalSummary, size: string, labels?: { readonly store: string; readonly article: string },
  /** The forecast id in words (`./forecast-words.ts`); a forecast this build does not know stays as the platform sent it. */
  forecastWords?: (modelVersionId: string) => string,
): Recommendation {
  const model = proposal.model_version === undefined || proposal.model_version === '' ? null : proposal.model_version;
  return {
    id: proposal.proposal_id,
    titleKey: 'inseason.why.proposalTitle',
    entityLabel: proposalGrain(proposal, size, labels),
    current: { kind: 'number', value: proposal.requested_units },
    proposed: { kind: 'number', value: proposal.units },
    // A dropped door's request is the need the run could not serve, never a request turned down.
    currentLabelKey: isDroppedDoorLine(proposal) ? DROPPED_DOOR_KEY.need : 'inseason.why.requested',
    proposedLabelKey: 'inseason.why.proposed',
    measure: PROPOSAL_UNITS,
    reasonCodes: (proposal.drivers ?? []).map(toDriverReason),
    reasonsAbsentKey: 'inseason.why.noDecomposition',
    // «4 driver», not the card's generic «4 reason codes»: the line carries a published column
    // called `reason_code`, drawn as a badge a few centimetres above, and these are not it.
    reasonsSummaryKey: 'inseason.why.driverCount',
    contributionUnitKey: 'inseason.unit.pairs',
    confidence: proposal.confidence ?? null,
    modelVersion: model !== null && forecastWords !== undefined && forecastModelKnown(model) ? forecastWords(model) : model,
    lineage: PROPOSAL_LINEAGE,
  };
}

/** A labelled fact inside an explanation, with the (i) of a standing rule where it has one. */
function Fact({ labelKey, aboutKeys, children }: {
  readonly labelKey: string;
  readonly aboutKeys?: readonly string[];
  readonly children: React.ReactNode;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-1">
      <dt className="text-caption text-ink-secondary inline-flex items-center gap-1">
        {t(labelKey)}
        {aboutKeys !== undefined && <AboutPopover aboutKeys={aboutKeys} title={t(labelKey)} size="sm" />}
      </dt>
      <dd className="text-body text-ink-primary">{children}</dd>
    </div>
  );
}

/** Money in the row's own currency, in the numeric face. */
export function Money({ value, currency }: { readonly value: number; readonly currency: string }): React.JSX.Element {
  const { i18n } = useTranslation();
  return (
    <span className="font-numeric text-numeric tabular-nums">{formatCurrency(value, currency, i18n.language)}</span>
  );
}

// ── where to go about a code ────────────────────────────────────────────────

/**
 * The three pieces below are shared by every surface that explains a proposal — the article
 * drawer on Proposals, the size opened on a store × article, and the «Perché» page — because
 * which of them a planner happens to work in is not a reason to know less (client request,
 * 2026-10-06). The drawer and the page differ in GRAIN, not in what is true: the drawer explains
 * one size, the page the row across its sizes, and both are bound by the same codes under the
 * same rules.
 */

/**
 * Where to go about each code: its governed parameters as links into the register, or the
 * sentence saying it is not a parameter at all (`./governing-parameters.ts`).
 *
 * One line PER CODE, named by the code, never a merged list. A row bound by the case rounding on
 * two sizes and by the door's capacity on one would otherwise offer three parameters with nothing
 * saying which belongs to which — and a planner who changes the wrong one changes nothing and
 * concludes the parameter does not work. A code this build does not name draws nothing: no route
 * is better than a wrong one.
 */
export function GovernedRoutes({ codes }: { readonly codes: readonly string[] }): React.JSX.Element | null {
  const { t } = useTranslation();
  // `flatMap` rather than `filter`, so the `unknown` case is narrowed away rather than asserted.
  const routed = [...new Set(codes)].flatMap((code) => {
    const governance = governanceOf(code);
    return governance.kind === 'unknown' ? [] : [{ code, governance }];
  });
  if (routed.length === 0) return null;
  return (
    <div className="flex flex-col gap-1 text-[12px]">
      <span className="text-ink-secondary">{t('inseason.governed.changeIt')}</span>
      {routed.map(({ code, governance }) => (
        <p key={code} data-why-governed={code} className="text-ink-secondary flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className="text-ink-primary">{t(toConstraintKey(code))}</span>
          {governance.kind === 'parameters' ? governance.keys.map((parameterKey) => (
            <InAppAnchor
              key={parameterKey}
              href={parameterHref(parameterKey)}
              className="text-ink-primary underline underline-offset-2"
            >
              {t(parameterLabelKey(parameterKey))}
            </InAppAnchor>
          )) : <span className="max-w-prose">{t(governance.noteKey)}</span>}
        </p>
      ))}
    </div>
  );
}

/** The codes whose floor is the shippable block, not the presentation minimum alone. */
const FLOOR_CODES: readonly string[] = ['pres_min_floor', 'below_presentation_min', 'presentation_minimum'];

/**
 * What the codes themselves do not say and a planner cannot infer. The two are drawn
 * DIFFERENTLY, because they are different kinds of fact:
 *
 *   • the precedence is a STANDING rule — `bound_by` names one thing, the tightest, and
 *     `allocation.engine._line_reason` takes protection, then the capacity cap, then the case
 *     rounding, then the floor, then demand rank. It is true of every line ever drawn, so it
 *     goes behind the (i) beside the heading, which is this repository's own idiom for exactly
 *     that (`AboutPopover`, used by every `Measure`). Inline, it would be a lecture repeated on
 *     every row;
 *   • the floor note is about THIS quantity — the floor is `round_up_to_case(max(presentation
 *     minimum, one per core size, protection floor, minimum order units, minimum transport
 *     units))` (`_shippable_block`), so a code naming the presentation minimum does NOT mean the
 *     presentation minimum won, and raising one of the five that is not the largest changes
 *     nothing. Drawn inline, and only where a floor code is actually on the line.
 *
 * `BoundByNotes` is therefore only the second. The first is `boundByAbout`, handed to the
 * heading's (i) by each surface.
 */
export const BOUND_BY_ABOUT_KEYS: readonly string[] = [
  // What the column means and what the service buffer is — the two «Perché» already carried on
  // this heading — and then the precedence, which neither of them says.
  'inseason.help.allocationAnatomy.colBoundBy',
  'inseason.help.allocationWhyScreen.serviceBuffer',
  'inseason.why.boundPrecedence',
];

export function BoundByNotes({ codes }: { readonly codes: readonly string[] }): React.JSX.Element | null {
  const { t } = useTranslation();
  if (!codes.some((code) => FLOOR_CODES.includes(code))) return null;
  return <p className="text-ink-secondary max-w-prose text-[12px]">{t('inseason.why.floorIsTheLargest')}</p>;
}

/** A heading with the (i) that carries a standing rule about it. */
export function HeadingAbout({ labelKey, aboutKeys }: {
  readonly labelKey: string;
  readonly aboutKeys: readonly string[];
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <span className="text-ink-secondary inline-flex items-center gap-1">
      {t(labelKey)}
      <AboutPopover aboutKeys={aboutKeys} title={t(labelKey)} size="sm" />
    </span>
  );
}

/**
 * Why THIS depth and not another — the one question no published figure answers.
 *
 * The engine maximises expected margin less end-of-season loss over the doors the constraints
 * leave free, under the supply budget (`planning.allocation.objective.marginal_value`, solved
 * exactly rather than heuristically because the coefficients are non-increasing).
 *
 * **Stated in one line, with the rest behind the (i).** It is a standing rule like the precedence
 * is, so by the rule above it belongs in a popover — but it is also the question the planner came
 * to this surface with, and a question answered only by clicking is not answered. So the rule
 * itself is one inline sentence and the (i) carries what qualifies it: the supply budget it is
 * solved under, and the fact that the objective's value for a single line is NOT published, which
 * is why no number is shown for any of this.
 */
export function ObjectiveNote(): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <p className="text-ink-secondary max-w-prose inline-flex flex-wrap items-baseline gap-1 text-[12px]">
      {t('inseason.why.objectiveRule')}
      <AboutPopover aboutKeys={['inseason.why.objectiveRuleDetail']} title={t('inseason.why.proposalTitle')} size="sm" />
    </p>
  );
}

// ── the rationing the line met ──────────────────────────────────────────────

/** The (i) beside the rationing: what the three groups are, and when they were measured. */
export const RATIONING_ABOUT_KEYS: readonly string[] = ['inseason.rationing.about', 'inseason.rationing.aboutMoment'];

/** A recorded group's absence, in the platform's reason as words — never a nought, never blank. */
export function RationingAbsent({ recorded }: { readonly recorded: Extract<Recorded<unknown>, { kind: 'absent' }> }): React.JSX.Element {
  const { t } = useTranslation();
  return <span className="text-ink-secondary" data-rationing-absent="">{t(recorded.reasonKey, recorded.reasonValues ?? {})}</span>;
}

/** «5° di 12 negozi» — the door's place in the engine's order for the option's DC pool. */
export function RationingRankValue({ recorded }: { readonly recorded: Recorded<{ readonly rank: number; readonly doors: number }> }): React.JSX.Element {
  const { t } = useTranslation();
  if (recorded.kind === 'absent') return <RationingAbsent recorded={recorded} />;
  return (
    <span className="font-numeric text-numeric tabular-nums" data-rationing-rank={recorded.value.rank}>
      {t('inseason.rationing.rankValue', { rank: recorded.value.rank, doors: recorded.value.doors })}
    </span>
  );
}

/**
 * Under «Vincolato da», what the run recorded about the rationing this line met (bata-platform
 * issue 603, ADR 6074): the door's place in the DC's order, the DC's pool of the size before / asked
 * across every door / after, and the size's share of the door's curve — each the platform's figure,
 * or its absent reason in words. Read as arithmetic, not as a story: «the DC had 4 pairs of 38 for 9
 * asked; this door was 5th of 12; 38 is 18 % of its curve».
 */
export function RationingFacts({ line }: { readonly line: ProposalSummary }): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const pool = dcPool(line);
  const share = sizeCurveShare(line);
  return (
    <div className="flex flex-col gap-1" data-rationing="">
      <HeadingAbout labelKey="inseason.rationing.title" aboutKeys={RATIONING_ABOUT_KEYS} />
      <dl className="grid gap-3 sm:grid-cols-3">
        <Fact labelKey="inseason.rationing.rank"><RationingRankValue recorded={rationingRank(line)} /></Fact>
        <Fact labelKey="inseason.rationing.pool">
          {pool.kind === 'absent' ? <RationingAbsent recorded={pool} /> : (
            <span className="font-numeric text-numeric tabular-nums" data-rationing-pool="">
              {t('inseason.rationing.poolValue', {
                before: formatInteger(pool.value.before, i18n.language),
                asked: formatInteger(pool.value.asked, i18n.language),
                after: formatInteger(pool.value.after, i18n.language),
              })}
            </span>
          )}
        </Fact>
        <Fact labelKey="inseason.rationing.share">
          {share.kind === 'absent' ? <RationingAbsent recorded={share} /> : (
            <span className="font-numeric text-numeric tabular-nums" data-rationing-share="">{formatPercent(share.value, i18n.language)}</span>
          )}
        </Fact>
      </dl>
    </div>
  );
}

// ── the answer in brief ─────────────────────────────────────────────────────

/** The phrases of the answer, under their heading. Nothing is drawn for a row with nothing to say. */
function AnswerBlock({ phrases }: { readonly phrases: readonly AnswerPhrase[] }): React.JSX.Element | null {
  const { t } = useTranslation();
  if (phrases.length === 0) return null;
  return (
    <section data-plain-answer="" className="bg-surface-sunken border-hairline flex flex-col gap-1 rounded-control border p-3">
      <h4 className="text-caption text-ink-secondary">{t('inseason.answer.title')}</h4>
      {phrases.map((entry) => (
        <p key={entry.id} data-answer={entry.id} className="text-body text-ink-primary max-w-prose">
          {t(entry.key, entry.values ?? {})}
        </p>
      ))}
    </section>
  );
}

/**
 * The ceiling per store and article as the run used it, read once from the run's provenance. A
 * failed or unpublished read leaves it unknown, which the answer states rather than guesses.
 */
function AnswerWithRunCap({ lines, runId }: { readonly lines: readonly ProposalSummary[]; readonly runId: string }): React.JSX.Element | null {
  const provenance = useApiQuery(runProvenance, { path: { run_id: runId } }, { staleTime: Number.POSITIVE_INFINITY });
  return <AnswerBlock phrases={plainAnswer(lines, capOfRun(provenance.data))} />;
}

/**
 * «In breve» — why the store receives this quantity, in plain words, at the head of every surface
 * that explains a proposal (the article drawer, the size opened on a store × article, and the
 * «Perché» page). `lines` are ALL the size lines of the store × article, because the engine decides
 * the total for the pair and splits it over the sizes afterwards (`./plain-answer.ts`). Without a
 * run there is no provenance to read, so the ceiling is unknown and no request is made.
 */
export function PlainAnswer({ lines, runId }: {
  readonly lines: readonly ProposalSummary[];
  readonly runId?: string | null | undefined;
}): React.JSX.Element | null {
  return runId === undefined || runId === null || runId === ''
    ? <AnswerBlock phrases={plainAnswer(lines, null)} />
    : <AnswerWithRunCap lines={lines} runId={runId} />;
}

/** Why one proposal has the quantity it has. */
export function ProposalExplain({ proposal, siblings, runId, labels, answerPending = false }: {
  readonly proposal: ProposalSummary;
  /** Every size line of the proposal's store × article in the run; the proposal alone where the surface holds no more. */
  readonly siblings?: readonly ProposalSummary[] | undefined;
  /** The run the proposal belongs to, for the ceiling it recorded. */
  readonly runId?: string | null | undefined;
  /** The store and the article by name beside the code (usability audit B2), where the caller has the run's names. */
  readonly labels?: { readonly store: string; readonly article: string } | undefined;
  /** The pair's other sizes are still being read: «In breve» states the pair's total, so it waits for them. */
  readonly answerPending?: boolean;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const sizeText = useProposalSizeText();
  const balance = driverBalance(proposal);
  const units = displayNumberFormat(i18n.language, { maximumFractionDigits: 0 });
  return (
    <RecommendationCard recommendation={proposalRecommendation(proposal, sizeText(proposal, false), labels, (id) => forecastModelWords(t, id))} reasonsOpen>
      {answerPending ? null : <PlainAnswer lines={rowLinesOf(proposal, siblings)} runId={runId} />}
      <dl className="grid gap-3 sm:grid-cols-3">
        <Fact labelKey="inseason.why.askedBecause"><Badge labelKey={toConstraintKey(proposal.reason_code)} /></Fact>
        <Fact labelKey="inseason.why.limitedBy" aboutKeys={BOUND_BY_ABOUT_KEYS}>
          <Badge labelKey={toConstraintKey(proposal.bound_by)} />
        </Fact>
        <Fact labelKey="inseason.column.expectedImpact">
          <Money value={proposal.expected_impact} currency={proposal.currency_code} />
        </Fact>
      </dl>
      {/* The two codes are the ASK and the BOUND, in that order and under those names: «Bound by»
          and «Reason code» are the column headers of the table, and on a card explaining one line
          they read as two synonyms for the same thing. What follows is where to go about each,
          and the two rules the codes cannot state themselves. */}
      <RationingFacts line={proposal} />
      <GovernedRoutes codes={[proposal.reason_code, proposal.bound_by]} />
      <BoundByNotes codes={[proposal.reason_code, proposal.bound_by]} />
      <ObjectiveNote />
      {balance.kind === 'balanced' && (
        <p className="text-caption text-ink-secondary">
          {t('inseason.why.balanced', { units: proposal.units })}
        </p>
      )}
      {balance.kind === 'unbalanced' && (
        <StatusIndicator
          level="serious"
          labelKey="inseason.why.unbalanced"
          values={{ sum: units.format(balance.sum), units: proposal.units }}
        />
      )}
    </RecommendationCard>
  );
}

// ── price steps ─────────────────────────────────────────────────────────────

/**
 * The markdown question for one price step's option, as a query string.
 *
 * The step names its option (`article_key`), cluster and channel, and those are set. The
 * market, brand, model version, horizon and as-at date are NOT on a price step, so whatever
 * the URL already carries is kept and nothing is supplied in their place — the Output view
 * names what is still missing rather than solving a question nobody posed.
 */
export function priceStepPathSearch(search: string, step: PriceActionSummary): URLSearchParams {
  const next = new URLSearchParams(search);
  next.delete(OPTION_KEY_PARAM_LEGACY);
  next.delete(COMPARE_KIND_PARAM);
  next.set(OPTION_KEYS_PARAM, step.article_key);
  next.set('clusterKey', step.cluster_key);
  next.set('channelKey', step.channel_key);
  // One option is posed, so no several-option drill is open, and the retired panel key goes
  // (issue 226: Result is the path's page; the key would only be redirected away).
  next.delete(OPEN_OPTION_PARAM);
  next.delete(MARKDOWN_VIEW_PARAM);
  return next;
}

const MARKDOWN_OUTPUT_PATH = '/inseason/markdown/output';
const MARKDOWN_EXPLANATION_PATH = '/inseason/markdown/explanation';

/**
 * Why one price step, and where its path is explained. With the run it belongs to, the step's own
 * recorded "why" — drivers, binding constraint, elasticity and version, the run's path — follows
 * (bata-ui#166 W4-2, `docs/adr/6126`).
 */
export function PriceStepExplain({ step, runId }: {
  readonly step: PriceActionSummary;
  readonly runId?: string;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  // A channel in the platform's words (`store`, `OUTLET`), the code where none is published (audit 2, E1).
  const channelLabel = useChannelLabel();
  const search = typeof window === 'undefined' ? '' : window.location.search;
  const toPath = (): void => navigateToScope(priceStepPathSearch(search, step), MARKDOWN_OUTPUT_PATH);
  const toExplanation = (): void => {
    const next = priceStepPathSearch(search, step);
    next.delete(EXPLAIN_PARAM);
    next.delete(EXPLAIN_VIEW_PARAM);
    navigateToScope(next, MARKDOWN_EXPLANATION_PATH);
  };
  return (
    <article className="bg-surface-raised border-hairline flex flex-col gap-3 rounded-control border p-3">
      <h4 className="text-subsection text-ink-primary">
        {t('inseason.why.priceTitle', { article: step.article_key, week: stepWhen(step, i18n.language) })}
      </h4>
      {/* A step priced on a fallback says so (bata-reqs issue 35, ADR 6024). */}
      <FlaggedBasisBadges step={step} />
      {/* Its target and realised % and its shelf price (bata-reqs issue 38 R4, platform ADR 6190). */}
      <PriceRoundingFacts value={step.rounding} currency={step.currency_code} />
      <dl className="grid gap-3 sm:grid-cols-3">
        <Fact labelKey="inseason.priceDecision.detail.from"><Money value={step.from_price} currency={step.currency_code} /></Fact>
        <Fact labelKey="inseason.priceDecision.detail.to"><Money value={step.to_price} currency={step.currency_code} /></Fact>
        <Fact labelKey="inseason.priceDecision.detail.depth">
          <span className="font-numeric text-numeric tabular-nums">{formatPercent(step.depth_from_full_price, i18n.language)}</span>
        </Fact>
        <Fact labelKey="inseason.priceDecision.detail.recovery">
          <Money value={step.expected_margin_recovery} currency={step.currency_code} />
        </Fact>
        <Fact labelKey="inseason.priceDecision.detail.terminal">
          <span className="font-numeric text-numeric tabular-nums">
            {t('inseason.priceDecision.detail.terminalValue', {
              projected: step.terminal_units_projected,
              target: step.terminal_target_units,
            })}
          </span>
        </Fact>
        <Fact labelKey="inseason.why.perimeter">
          <span translate="no">{t('inseason.why.perimeterValue', { cluster: step.cluster_key, channel: channelLabel(step.channel_key) })}</span>
        </Fact>
      </dl>
      <p className="text-caption text-ink-secondary max-w-prose">{t('inseason.why.priceScope')}</p>
      <div className="flex flex-wrap gap-2">
        <Button variant="secondary" size="sm" icon={<ArrowRight size={14} aria-hidden />} onClick={toPath}>
          {t('inseason.why.openPath')}
        </Button>
        <Button variant="secondary" size="sm" icon={<ArrowRight size={14} aria-hidden />} onClick={toExplanation}>
          {t('inseason.why.openElasticity')}
        </Button>
      </div>
      <p className="text-caption text-ink-secondary max-w-prose">{t('inseason.why.diagnoseAbsent')}</p>
      {runId === undefined ? null : <PriceStepWhy runId={runId} priceActionId={step.price_action_id} />}
    </article>
  );
}

// ── transfers ───────────────────────────────────────────────────────────────

/**
 * A transfer's reference label — article, size and route, as data (G6). `size` is the size as
 * words (`useSizeText()` over `sizeName(transfer)`), never the opaque `size_key`.
 */
export function transferLabel(transfer: TransferSummary, size: string): string {
  return [transfer.article_key, size, transfer.donor_store_key, transfer.recipient_store_key].join(' · ');
}

/** A price step's reference label — article, cluster, channel and week, as data (G6). */
export function priceStepLabel(step: PriceActionSummary): string {
  return [step.article_key, step.cluster_key, step.channel_key, step.effective_week].join(' · ');
}

/**
 * Why one transfer: the three figures that justified it, in the row's currency — and, with the run
 * it belongs to, both doors' stock and cover as the run read them and what bound the line
 * (bata-ui#166 W4-2, `docs/adr/6126`).
 */
export function TransferExplain({ transfer, runId }: {
  readonly transfer: TransferSummary;
  readonly runId?: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const sizeText = useSizeText();
  return (
    <article className="bg-surface-raised border-hairline flex flex-col gap-3 rounded-control border p-3">
      <h4 className="text-subsection text-ink-primary">
        {t('inseason.why.transferTitle', {
          units: transfer.units,
          article: transfer.article_key,
          size: sizeText(sizeName(transfer), false),
        })}
      </h4>
      <dl className="grid gap-3 sm:grid-cols-3">
        <Fact labelKey="inseason.transfers.routeHeader">
          <span translate="no">
            {t('inseason.transfers.route', { donor: transfer.donor_store_key, recipient: transfer.recipient_store_key })}
          </span>
        </Fact>
        <Fact labelKey="inseason.transfers.purposeHeader"><Badge labelKey={toPurposeKey(transfer.purpose)} /></Fact>
        <Fact labelKey="inseason.transferDecision.column.disposition">
          <Badge labelKey={dispositionStateKey(transfer.disposition ?? null)} />
        </Fact>
        <Fact labelKey="inseason.column.laneCost"><Money value={transfer.lane_cost} currency={transfer.currency_code} /></Fact>
        <Fact labelKey="inseason.column.runDamage"><Money value={transfer.run_damage} currency={transfer.currency_code} /></Fact>
        <Fact labelKey="inseason.why.netBenefit"><Money value={transfer.net_benefit} currency={transfer.currency_code} /></Fact>
      </dl>
      <p className="text-caption text-ink-secondary max-w-prose">{t('inseason.why.transferReading')}</p>
      {runId === undefined ? null : <TransferWhy runId={runId} transferId={transfer.transfer_id} />}
    </article>
  );
}

// ── the view ────────────────────────────────────────────────────────────────

/** How many of the queue's lines the explain view offers before pointing at the table. */
export const EXPLAIN_PICKER_LIMIT = 10;

export interface ExplainLine {
  readonly id: string;
  /** Reference data — keys and codes, never translated (G6). */
  readonly label: string;
}

export interface ExplainViewProps {
  /** The queue's lines, in the order the read returned them. */
  readonly lines: readonly ExplainLine[];
  /** The line whose explanation is open, or `null` for the list. */
  readonly itemId: string | null;
  /** The explanation of the open line — `null` when the id matches no line in this run. */
  readonly explanation: React.ReactNode | null;
  /** How many lines the queue holds, where `lines` are only its first (a queue the platform pages). */
  readonly total?: number;
}

/**
 * The explain view at the head of a queue: one line's explanation, or the list to choose from.
 *
 * The list is the first lines in the read's order — the platform's ranking — and says that
 * every other line is opened from the table below. Nothing is opened on the planner's behalf.
 */
export function ExplainView({ lines, itemId, explanation, total }: ExplainViewProps): React.JSX.Element {
  const { t } = useTranslation();
  const shown = lines.slice(0, EXPLAIN_PICKER_LIMIT);
  return (
    <Panel
      titleKey="inseason.why.title"
      aboutKey="inseason.why.about"
      action={(
        <Button variant="ghost" size="sm" icon={<X size={14} aria-hidden />} onClick={closeExplain}>
          {t('inseason.why.close')}
        </Button>
      )}
    >
      <div className="flex flex-col gap-3" data-explain={itemId ?? 'list'}>
        {itemId !== null && explanation !== null ? (
          <>
            {explanation}
            <div>
              <Button variant="ghost" size="sm" onClick={() => openExplain(null)}>{t('inseason.why.backToList')}</Button>
            </div>
          </>
        ) : (
          <>
            {itemId !== null && (
              <StatusIndicator level="warning" labelKey="inseason.why.notInRun" />
            )}
            {lines.length === 0 ? (
              <p className="text-body text-ink-secondary">{t('inseason.why.nothingQueued')}</p>
            ) : (
              <>
                <p className="text-caption text-ink-secondary max-w-prose">
                  {t('inseason.why.pick', { shown: shown.length, total: total ?? lines.length })}
                </p>
                <ul className="flex flex-col gap-1">
                  {shown.map((line) => (
                    <li key={line.id}>
                      <Button variant="ghost" size="sm" onClick={() => openExplain(line.id)}>
                        {t('inseason.why.open', { line: line.label })}
                      </Button>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </>
        )}
      </div>
    </Panel>
  );
}

/** The per-row way in, for a queue table: "Why this" on one line. */
export function ExplainRowButton({ id, label }: ExplainLine): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <Button variant="ghost" size="sm" onClick={() => openExplain(id)} aria-label={t('inseason.why.open', { line: label })}>
      {t('inseason.why.rowAction')}
    </Button>
  );
}

/**
 * The price queue's explain view, above Pricing › Pending approvals.
 *
 * It reads what it shows, never the run's steps (`docs/adr/9076`, bata-platform ADR 6215): the first
 * {@link EXPLAIN_PICKER_LIMIT} steps of the run in the platform's order, with how many there are, and the ONE step
 * opened, by its id. The centred loading cue stands while either is read.
 */
export function PriceStepExplainView(
  { runId, itemId }: { readonly runId: string; readonly itemId: string | null },
): React.JSX.Element {
  const first = useApiQuery(rankedPriceStepsReport, { path: { run_id: runId }, query: { rank: 'effective_week', limit: EXPLAIN_PICKER_LIMIT } });
  const opened = useApiQuery(
    rankedPriceStepsReport,
    { path: { run_id: runId }, query: { price_action_id: [itemId ?? ''], limit: 1 } },
    { enabled: itemId !== null },
  );
  if (first.data === undefined || (itemId !== null && opened.data === undefined && !opened.isError)) {
    return <Skeleton rows={3} busy extent="section" />;
  }
  const open = itemId === null ? undefined : opened.data?.items.find((step) => step.price_action_id === itemId);
  return (
    <ExplainView
      lines={first.data.items.map((step) => ({ id: step.price_action_id, label: priceStepLabel(step) }))}
      total={first.data.total}
      itemId={itemId}
      explanation={open === undefined ? null : <PriceStepExplain step={open} runId={runId} />}
    />
  );
}
